import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import {
  WSActions,
  WSCard,
  WSControl,
  WSControlGroupCards,
  WSEmptyState,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { WSQueryCache } from "@ws-react-query";
import { useCallback, useState } from "react";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_PAYOUT_SETTINGS } from "../../../../query/payments/keys";
import { paymentsService } from "../../../../services/payments";
import {
  avatarBankAccount,
  avatarBankCard
} from "../../../../shared/constants/avatars";
import { WSServiceError } from "../../../../utils/serviceHelper";

type UnavailableSelection = "debit-card" | "try-another-bank";

type Props = {
  failedAccount?: IAccount;
  fee: number;
  onBack: () => void;
  onDebitCard: () => void;
  onTryAnotherBank: () => void;
  onStandardPayoutSet?: () => void;
};

export const RouteInstantUnavailable: React.FC<Props> = ({
  failedAccount,
  fee,
  onBack,
  onDebitCard,
  onTryAnotherBank,
  onStandardPayoutSet
}) => {
  const userId = useUserId();
  const [selectedOption, setSelectedOption] = useState<
    UnavailableSelection | undefined
  >(undefined);
  const [useAsStandard, setUseAsStandard] = useState(false);

  const [setStandardPayout, setStandardPayoutMeta] = useWSMutation<
    void,
    WSServiceError
  >(
    async () => {
      if (!failedAccount) {
        return;
      }

      const payoutSettings = await paymentsService.payoutSettings.update(
        userId,
        {
          payoutPreferences: PayoutPreferences.Standard,
          standard: {
            destinationId: failedAccount.accountId,
            destinationType: PayoutDestinationType.Account
          }
        }
      );

      WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
    },
    {
      onSuccess: onStandardPayoutSet,
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const handleContinue = useCallback(() => {
    if (useAsStandard) {
      setStandardPayout();
      return;
    }

    if (selectedOption === "debit-card") {
      onDebitCard();
    } else if (selectedOption === "try-another-bank") {
      onTryAnotherBank();
    }
  }, [
    useAsStandard,
    selectedOption,
    setStandardPayout,
    onDebitCard,
    onTryAnotherBank
  ]);

  const isSelectionMade = useAsStandard || selectedOption !== undefined;

  return (
    <WSList gap="2XL">
      <WSEmptyState
        type="search"
        title="Instant payout unavailable"
        description="This bank does not support Real-Time Payments. Try another method instead."
        bgColor="white"
        orientation="horizontal"
        hasBorder
      />

      <WSCard
        header={{
          label: {
            text: "Try another method"
          }
        }}
      >
        <WSList gap="2XL">
          <WSText.ParagraphSm color="gray500">
            Connect a debit card, try another bank or switch to standard payout.
          </WSText.ParagraphSm>

          <WSControlGroupCards
            value={selectedOption ?? ""}
            onChange={newValue => {
              setSelectedOption(newValue as UnavailableSelection);
              setUseAsStandard(false);
            }}
            options={[
              {
                header: {
                  label: {
                    text: `Connect a debit card (${fee}% fee)`,
                    helperText:
                      "Receive payments by connecting an eligible Visa or Mastercard debit card",
                    avatar: avatarBankCard
                  }
                },
                value: "debit-card"
              },
              {
                header: {
                  label: {
                    text: "Try another bank",
                    helperText:
                      "Try instant payout by connecting an account from another bank",
                    avatar: avatarBankAccount
                  }
                },
                value: "try-another-bank"
              }
            ]}
          />

          <WSInfoBox theme="info">
            <WSText.ParagraphXs mb="L" color="gray500">
              If you wish to use the bank account you provided as a{" "}
              <b>standard payout method</b> instead, check the box below.
            </WSText.ParagraphXs>

            <WSControl
              type="checkbox"
              value={useAsStandard}
              onChange={checked => {
                setUseAsStandard(checked);
                if (checked) {
                  setSelectedOption(undefined);
                }
              }}
              label="Add this bank account and receive you funds in the standard 2-5 business days."
              size="S"
            />
          </WSInfoBox>
        </WSList>
      </WSCard>

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Back",
            kind: "Tertiary",
            onClick: onBack
          },
          {
            label: "Continue",
            disabled: !isSelectionMade,
            loading: setStandardPayoutMeta.isLoading,
            onClick: handleContinue
          }
        ]}
      />
    </WSList>
  );
};
